import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { documentService } from '@/services/documentService'
import type { Document, DocumentInsert, DocumentUpdate } from '@/types/database'

export const DOCUMENTS_QUERY_KEY = ['documents']
export const DOCUMENT_VERSIONS_QUERY_KEY = ['document-versions']

export function useDocuments() {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEY,
    queryFn: () => documentService.getDocuments(),
  })
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, id],
    queryFn: () => documentService.getDocument(id),
    enabled: !!id,
  })
}

export function useDocumentsByProduct(productId: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'product', productId],
    queryFn: () => documentService.getDocumentsByProduct(productId),
    enabled: !!productId,
  })
}

export function useDocumentsByComponent(componentId: string) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'component', componentId],
    queryFn: () => documentService.getDocumentsByComponent(componentId),
    enabled: !!componentId,
  })
}

export function useExpiringDocuments(withinDays: number = 30) {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'expiring', withinDays],
    queryFn: () => documentService.getExpiringDocuments(withinDays),
  })
}

export function useDocumentStats() {
  return useQuery({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'stats'],
    queryFn: () => documentService.getDocumentStats(),
  })
}

export function useCreateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (document: Omit<DocumentInsert, 'organization_id'>) =>
      documentService.createDocument(document),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      file,
      metadata,
    }: {
      file: File
      metadata: Omit<DocumentInsert, 'organization_id' | 'file_path' | 'file_size' | 'mime_type'>
    }): Promise<Document | null> => {
      return documentService.uploadDocument(file, metadata)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: DocumentUpdate }) =>
      documentService.updateDocument(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_QUERY_KEY, id] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

// =====================================================
// Document Versioning Hooks
// =====================================================

export function useDocumentVersions(documentId: string) {
  return useQuery({
    queryKey: [...DOCUMENT_VERSIONS_QUERY_KEY, documentId],
    queryFn: () => documentService.getVersionHistory(documentId),
    enabled: !!documentId,
  })
}

export function useUploadNewVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      documentId,
      file,
      changeNotes,
    }: {
      documentId: string
      file: File
      changeNotes: string
    }) => documentService.uploadNewVersion(documentId, file, changeNotes),
    onSuccess: (_, { documentId }) => {
      // Invalidate both the document and its versions
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...DOCUMENT_VERSIONS_QUERY_KEY, documentId] })
    },
  })
}

export function useSetCurrentVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      documentId,
      versionId,
    }: {
      documentId: string
      versionId: string
    }) => documentService.setCurrentVersion(documentId, versionId),
    onSuccess: (_, { documentId }) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...DOCUMENT_VERSIONS_QUERY_KEY, documentId] })
    },
  })
}

export function useDownloadVersion() {
  return useMutation({
    mutationFn: ({
      versionId,
      fileName,
    }: {
      versionId: string
      fileName: string
    }) => documentService.downloadVersion(versionId).then(blob => {
      if (!blob) throw new Error('Failed to download version')

      // Trigger download
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }),
  })
}
